import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, Sparkles, LayoutDashboard } from 'lucide-react';
import { useAuthStore } from '../../stores/auth';
import { dealerTierFor, normalizeRoleName } from '../../lib/roles';
import { resolveRoleKey } from '../../pages/dashboard/roleLayouts';
import { useFieldProgramConfig } from '../../hooks/useFieldProgramConfig';
import { useMyPermissions } from '../../hooks/useMyPermissions';
import { resolveProfile, isPlatformAdmin, PROFILE_LABELS, isSalesIdentity } from '../../lib/profileIdentity';
import { previewIdentity } from '../../lib/profilePreview';
import { profileMenu } from '../../lib/profileMenus';
import { SECTIONS_BY_VIEW, VIEW_LABELS, SALES_ACTIVITY_SECTION, roleKeyToView, filterItems, withDealerManagementNavigation, routeOnly, type SidebarView, type NavSection, type NavItem } from './sidebarRegistry';
export { SECTIONS_BY_VIEW, roleKeyToView, SIDEBAR_VIEWS, SIDEBAR_VIEW_GROUPS, type SidebarView } from './sidebarRegistry';

export interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  /**
   * Optional view override from TopBar dept-dropdown (admin-only).
   * If set, takes priority over the user's resolved role.
   */
  viewOverride?: SidebarView | null;
  /**
   * Mobile only — called after a nav link tap so the drawer closes.
   * Wired by AppShell when rendering the mobile drawer overlay.
   */
  onMobileClose?: () => void;
}

export function Sidebar({ open, onToggle: _onToggle, viewOverride, onMobileClose }: SidebarProps) {
  const { user, tenant } = useAuthStore();
  const { config: fpConfig, enabled: fpEnabled } = useFieldProgramConfig();
  // Phase 5 — affordance only; every route and API is still gated server-side.
  const { permissions } = useMyPermissions();

  const presentation = previewIdentity(user, viewOverride) ?? user;
  const roleName = normalizeRoleName(presentation?.role?.name);
  const isSuperAdmin = isPlatformAdmin(user);
  const profile = resolveProfile(presentation);
  const roleKey = useMemo(() => resolveRoleKey(presentation as never), [presentation]);

  // Determine active view — super-admin > explicit override > role-resolved
  const activeView: SidebarView = isSuperAdmin
    ? 'super_admin'
    : roleKeyToView(roleKey, roleName);

  const enabledModules = useMemo<Set<string> | null>(() => {
    if (!tenant?.enabledModules) return null; // null = all enabled
    return new Set(tenant.enabledModules);
  }, [tenant]);

  // See-more drawer state — persisted per-role
  const moreStorageKey = `aura-sidebar-more-${activeView}`;
  const [seeMoreOpen, setSeeMoreOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(moreStorageKey) === '1';
  });
  useEffect(() => {
    // Re-load on view change
    if (typeof window !== 'undefined') {
      setSeeMoreOpen(localStorage.getItem(moreStorageKey) === '1');
    }
  }, [moreStorageKey]);
  const toggleSeeMore = () => {
    setSeeMoreOpen((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem(moreStorageKey, next ? '1' : '0');
      }
      return next;
    });
  };

  // Compose sections
  const baseSections = SECTIONS_BY_VIEW[activeView] ?? [];
  const dealerTier = dealerTierFor(roleName);

  const planTier = tenant?.planTier ?? null;
  // LBR2-G1 — per-tenant capabilities (default OFF); gates SS-only nav items.
  const capabilities = tenant?.capabilities ?? null;

  const sections = useMemo<NavSection[]>(() => {
    if (profile && !isSuperAdmin) {
      return profileMenu(profile).map(section => ({ ...section, items: section.items.map(item => ({ ...item, icon: item.icon ?? LayoutDashboard })) }));
    }
    return withDealerManagementNavigation(baseSections, dealerTier)
      .map((section) => {
        const core = filterItems(section.items.filter(item => isSalesIdentity(presentation) || item.to !== '/scorecard'), enabledModules, roleName, planTier, capabilities, permissions);
        const more = section.seeMore
          ? filterItems(section.seeMore.filter(item => isSalesIdentity(presentation) || item.to !== '/scorecard'), enabledModules, roleName, planTier, capabilities, permissions)
          : undefined;
        return { label: section.label, items: core, seeMore: more };
      })
      // Pre-existing and load-bearing for Phase 5: a section whose every item
      // was filtered out is dropped, so a permission gate never leaves a bare
      // header behind.
      .filter((s) => s.items.length > 0 || (s.seeMore && s.seeMore.length > 0));
  }, [presentation, profile, isSuperAdmin, baseSections, dealerTier, enabledModules, roleName, planTier, capabilities, permissions]);

  // TODO(D-09 tenant-leak, Apr 22 2026): Dynamic "Sun Stellar Plumber Connect"
  // section hid from sidebar because fpConfig.appName leaks tenant brand across
  // roles. Field-Partner routes remain reachable via Settings → Modules →
  // Field Partners (gated by enabledModules) and via direct URL. Revisit with
  // a proper role-gated + module-gated rework. Do NOT re-enable without that.
  // See docs/ui-rebuild/DEFERRED-FIELD-PARTNERS-SIDEBAR.md for the full plan.
  // Note: useFieldProgramConfig() hook call above intentionally kept — other
  // consumers (FieldProgramGate, field-partner pages) depend on it.
  const fieldPartnersSection: NavSection | null = null;

  const personalSales = isSalesIdentity(presentation) ? [SALES_ACTIVITY_SECTION] : [];
  const allSections = fieldPartnersSection
    ? [...personalSales, ...sections, fieldPartnersSection]
    : [...personalSales, ...sections];

  // Last section — attach see-more chevron here if drawer exists
  const lastSectionIdx = allSections.length - 1;

  return (
    <aside
      className={`flex flex-col h-full min-h-0 border-r transition-all duration-200 ${
        open ? 'w-60' : 'w-16'
      }`}
      style={{
        background: 'var(--ivory-1)',
        borderColor: 'var(--line)',
        flexShrink: 0,
      }}
    >
      {/* Role tag header */}
      <div
        className="flex items-center gap-2 px-3 pt-3 pb-2"
        style={{ color: 'var(--ink-3)' }}
      >
        {open && (
          <span
            className="text-[10px] font-medium uppercase tracking-wider"
          >
            {profile && !isSuperAdmin ? PROFILE_LABELS[profile] : VIEW_LABELS[activeView]}
          </span>
        )}
      </div>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto px-2 pb-2">
        {allSections.map((section, sIdx) => {
          const isLast = sIdx === lastSectionIdx;
          const hasSeeMore = isLast && section.seeMore && section.seeMore.length > 0;
          return (
            <div key={section.label} className="mb-3">
              {open && (
                <p
                  className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider"
                  style={{ color: 'var(--ink-4)' }}
                >
                  {section.label}
                </p>
              )}
              {section.items.map((item) => (
                <SidebarLink key={`${section.label}-${item.to}`} item={item} collapsed={!open} onMobileClose={onMobileClose} />
              ))}
              {hasSeeMore && (
                <div className="mt-1">
                  {open && (
                    <button
                      type="button"
                      onClick={toggleSeeMore}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium transition-colors"
                      style={{
                        color: 'var(--ink-3)',
                        background: 'transparent',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ivory-2)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      {seeMoreOpen
                        ? <ChevronDown size={13} />
                        : <ChevronRight size={13} />
                      }
                      <span>{seeMoreOpen ? 'See less' : 'See more'}</span>
                    </button>
                  )}
                  {seeMoreOpen && section.seeMore!.map((item) => (
                    <SidebarLink
                      key={`more-${item.to}`}
                      item={item}
                      collapsed={!open}
                      dimmed
                      onMobileClose={onMobileClose}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* AI summary card footer — only in expanded mode, and hidden for
          strict-view roles that don't have AI panel access (May 26, 2026). */}
      {open && (!profile || profile === 'P18') && activeView !== 'lead_triage' && (
        <div
          className="m-2 rounded-md border p-3"
          style={{
            background: 'linear-gradient(180deg, var(--saffron-50), var(--saffron-100))',
            borderColor: 'var(--saffron-100)',
          }}
        >
          <div className="mb-1 flex items-center gap-1.5" style={{ color: 'var(--saffron-600)' }}>
            <Sparkles size={12} />
            <span className="text-[11px] font-semibold">Weekly AI summary</span>
          </div>
          <p className="text-[11px] leading-snug" style={{ color: 'var(--ink-2)' }}>
            Open the AI panel (⌘J) for insights tailored to your role.
          </p>
        </div>
      )}
    </aside>
  );
}

/* ------------------------------------------------------------------ */
/*  Link                                                                */
/* ------------------------------------------------------------------ */

function SidebarLink({
  item,
  collapsed,
  dimmed = false,
  onMobileClose,
}: {
  item: NavItem;
  collapsed: boolean;
  dimmed?: boolean;
  onMobileClose?: () => void;
}) {
  const location = useLocation();
  const expectedTab = new URLSearchParams(item.to.split('?')[1]).get('tab');
  const queryMatches = !expectedTab || new URLSearchParams(location.search).get('tab') === expectedTab;
  const IconCmp = item.icon;
  const route = routeOnly(item.to);
  return (
    <NavLink
      to={item.to}
      end={route === '/dashboard'}
      title={collapsed ? item.label : undefined}
      onClick={onMobileClose}
      className={({ isActive }) =>
        `flex items-center gap-2.5 rounded-md px-2 py-1.5 text-[13px] transition-colors my-0.5 ${
          collapsed ? 'justify-center' : ''
        }`
      }
      style={({ isActive }) => ({
        background: isActive && queryMatches ? 'var(--ivory-2)' : 'transparent',
        color: isActive && queryMatches ? 'var(--ink-0)' : dimmed ? 'var(--ink-3)' : 'var(--ink-1)',
        fontWeight: isActive && queryMatches ? 500 : 400,
        position: 'relative',
      }) as React.CSSProperties}
      onMouseEnter={(e) => {
        if (!e.currentTarget.classList.contains('active')) {
          e.currentTarget.style.background = 'var(--ivory-2)';
        }
      }}
      onMouseLeave={(e) => {
        // Preserve active state on leave via style recompute — NavLink re-renders anyway
        const isActive = e.currentTarget.getAttribute('aria-current') === 'page';
        if (!isActive) e.currentTarget.style.background = 'transparent';
      }}
    >
      <IconCmp size={15} style={{ flexShrink: 0 }} />
      {!collapsed && <span className="flex-1 text-left">{item.label}</span>}
      {!collapsed && typeof item.count === 'number' && (
        <span
          className="rounded-full px-1.5 text-[10px] tabular-nums"
          style={{ background: 'var(--ivory-2)', color: 'var(--ink-3)' }}
        >
          {item.count}
        </span>
      )}
    </NavLink>
  );
}

/* ------------------------------------------------------------------ */
/*  Named helpers (for TopBar dept-dropdown)                           */
/* ------------------------------------------------------------------ */
